import { inject } from '@angular/core';
import { ResolveFn, Router } from '@angular/router';
import { Location } from '@angular/common';
import { RESOURCES } from '@pxl/shared/translation';
import { from, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { LoginService } from '@pxl/models/contracts';
import { AccountRoutingRouteNames, AppRoutingRouteNames, DashboardRoutingRouteNames } from '@pxl/route-names';
import { ButtonType } from '@pxl/models/common';

import { GeneralErrorButtonData, GeneralErrorData } from '../models/general-error.models';

// Helper: parse the status code from route or navigation state and default to 404 if missing/invalid
function parseErrorCode(route: any, router: Router, defaultCode: number): number {
    const codeParam = route?.queryParamMap?.get?.('code') ?? router.currentNavigation()?.extras.state?.['code'];
    const parsed = Number(codeParam);
    return Number.isFinite(parsed) ? parsed : defaultCode;
}

// Helper: build common buttons
function makeDashboardButton(router: Router): GeneralErrorButtonData {
    return {
        label: RESOURCES.petsxl.ui.page_not_found.actions.dashboard,
        type: ButtonType.primary,
        action: () => router.navigate([DashboardRoutingRouteNames.Dashboard]),
    };
}

function makeRegistrationButton(router: Router): GeneralErrorButtonData {
    return {
        label: RESOURCES.petsxl.ui.page_not_found.actions.registration,
        type: ButtonType.secondary,
        action: () => router.navigate([AppRoutingRouteNames.SignIn, AccountRoutingRouteNames.SignUpRegistration]),
    };
}

function makeLoginButton(router: Router): GeneralErrorButtonData {
    return {
        label: RESOURCES.petsxl.ui.page_not_found.actions.login,
        type: ButtonType.primary,
        action: () => router.navigate([AppRoutingRouteNames.SignIn]),
    };
}

function makeBackButton(location: Location): GeneralErrorButtonData {
    return {
        label: RESOURCES.petsxl.ui.shared.pxl.pxl_modal.buttons.back,
        type: ButtonType.secondary,
        action: () => location.back(),
    };
}

// Helper: build data models
function buildData404(
    stateTitle: string | undefined,
    stateMessage: string | undefined,
    isLoggedIn: boolean,
    router: Router,
): GeneralErrorData {
    const resourceKeys = RESOURCES.petsxl.ui.page_not_found;
    const dashboardButton = makeDashboardButton(router);
    const registrationButton = makeRegistrationButton(router);
    const loginButton = makeLoginButton(router);

    return {
        title: stateTitle || '',
        message: stateMessage || resourceKeys.headline,
        buttons: isLoggedIn ? [dashboardButton] : [loginButton, registrationButton],
        pageHeaderIllustration: 'error-message',
    };
}

function buildData5xx(
    stateTitle: string | undefined,
    stateMessage: string | undefined,
    isLoggedIn: boolean,
    router: Router,
    location: Location,
): GeneralErrorData {
    const resourceKeys = RESOURCES.petsxl.ui.global.errors.internal_server_error;
    const backButton = makeBackButton(location);
    const dashboardButton = makeDashboardButton(router);
    const registrationButton = makeRegistrationButton(router);
    const loginButton = makeLoginButton(router);

    const buttons = isLoggedIn ? [backButton, dashboardButton] : [backButton, loginButton, registrationButton];

    return {
        title: stateTitle || resourceKeys.title,
        message: stateMessage || resourceKeys.sub_title,
        buttons,
        pageHeaderIllustration: 'error-message',
    };
}

export const resolveGeneralErrorPageData: ResolveFn<Observable<GeneralErrorData>> = (route) => {
    const router = inject(Router);
    const location = inject(Location);
    const loginService = inject(LoginService);

    const code = parseErrorCode(route, router, 404);

    const nav = router.currentNavigation();
    const stateTitle = nav?.extras.state?.['title'];
    const stateMessage = nav?.extras.state?.['message'];

    return from(loginService.isLoggedIn()).pipe(
        map((isLoggedIn) => {
            switch (code) {
                case 500:
                    return buildData5xx(stateTitle, stateMessage, isLoggedIn, router, location);
                case 404:
                default:
                    // Fallback to 404 behavior for all other/unknown codes
                    return buildData404(stateTitle, stateMessage, isLoggedIn, router);
            }
        }),
    );
};
